import { isDefined } from '@sgme/fp';
import classnames from 'classnames';
import { OnKeyDownArg } from 'components/common-form/builder/form/node/field/Grid/Keys';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useFieldValidationError } from 'components/common-form/hooks/useFieldValidationError';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import type { DatePickerSize } from 'components/shared/DatePicker/DatePicker';
import { MonoDatePickerWithCustomPreset } from 'components/shared/DatePicker/MonoDatePicker';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { MessageValue } from 'contexts/intl/intlContext';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import { PresetDateFieldData, PresetDateFieldMetadata } from 'models/forms/fields/PresetDateFieldMetadata';
import { useCallback, useContext, useMemo } from 'react';
import type { WorkspaceType } from 'state/workspaces/model';
import { getDateOnlyFromIsoString } from 'utils/date';
import type { TranslatableContent } from 'utils/intl';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export function PresetDateField({ node: { id }, className, workspaceType, tabId, tabIndex }: ProductNodeBuilderProps) {
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <PresetDateFieldPicker
        id={id}
        tabId={tabId}
        className={className}
        tabIndex={tabIndex}
        workspaceType={workspaceType}
      />
    </BlockOrientationFormGroup>
  );
}

type FieldDatePickerProps = {
  id: string;
  size?: DatePickerSize;
  className?: string;
  tabId?: string;
  hasError?: boolean;
  tabIndex?: number;
  workspaceType?: WorkspaceType;
};
interface PresetTranslationValue extends Record<string, MessageValue> {
  count: number;
}
export function PresetDateFieldPicker({
  id,
  size,
  className,
  tabId,
  hasError,
  tabIndex,
  workspaceType,
}: FieldDatePickerProps) {
  const { formatDisplayedDate } = useSPIntl();
  const [presetDate, setPresetDate] = useData<PresetDateFieldData>(id);
  const isFieldInvalid = useFieldValidationError(id)?.severity !== undefined;
  const isReadonly = useBlockOrFieldReadOnly(id);
  const { isReadOnly, presets = [], presetType } = useFieldMetadata<PresetDateFieldMetadata>(id)!;
  const { isCellInactive, isFocused, lostInputBeforeEditing, onEditingKeyDown, isSelectAll } =
    useContext(BatchNavigationContext);

  const formattedDate = getDateOnlyFromIsoString(presetDate?.date);
  const readOnlyFormattedValue = isDefined(presetDate?.date) ? formatDisplayedDate(presetDate?.date) : '';

  const customPresetTranslationKeys: TranslatableContent<PresetTranslationValue>[] | undefined = useMemo(() => {
    return presets
      .map(preset => {
        return {
          id: `date.presets.${presetType}` as IntlKey,
          values: { count: preset } satisfies PresetTranslationValue,
        } satisfies TranslatableContent<PresetTranslationValue>;
      })
      .filter(isDefined);
  }, [presets, presetType]);

  const handleKeyDown = useCallback(
    ({ e, commit }: OnKeyDownArg) => {
      if (onEditingKeyDown) {
        onEditingKeyDown(e);
        switch (e.key) {
          case 'Enter':
          case 'Tab':
            commit();
            break;
          default:
            break;
        }
      }
    },
    [onEditingKeyDown],
  );

  if (isCellInactive || isReadOnly) {
    return (
      <input
        className={'form-control pe-none'}
        data-e2e={id + '-input'}
        id={id}
        name={id}
        readOnly={isReadOnly}
        type="text"
        value={readOnlyFormattedValue}
        tabIndex={tabIndex}
        disabled={isReadOnly}
      />
    );
  }

  return (
    <MonoDatePickerWithCustomPreset
      className={classnames(className)}
      size={size}
      value={formattedDate as Nullable<string>}
      id={id}
      onPresetChange={preset =>
        setPresetDate({
          date: presetDate!.date,
          offset: (preset as TranslatableContent<PresetTranslationValue>).values!.count,
        })
      }
      disabled={isReadonly}
      isClearable={false}
      hasError={isFieldInvalid || hasError === true}
      tabId={tabId}
      tabIndex={tabIndex}
      availableDates={[presetDate?.date!]}
      isFocused={isFocused}
      onKeyDown={handleKeyDown}
      workspaceType={workspaceType}
      inputValue={lostInputBeforeEditing}
      isSelectAll={isSelectAll}
      datePresetTranslations={customPresetTranslationKeys}
      hidePresets={false}
    />
  );
}
